import { useCallback } from 'react';
import { useFetch } from '@/shared/hooks/useFetch/useFetch';
import { fetcher } from '@/shared/utils/api/fetcher';
import type { NotificationsResponse, UnreadCountResponse } from './useNotifications.types';

export const useNotifications = (refreshInterval = 60000, isOpen = false) => {
  const { data: countData, mutate: mutateCount } = useFetch<UnreadCountResponse>({
    url: '/v1/account/notifications/unread-count',
    requiresAuth: true,
    swrConfig: { refreshInterval },
  });

  const {
    data: listData,
    isLoading,
    mutate: mutateList,
  } = useFetch<NotificationsResponse>({
    url: isOpen ? '/v1/account/notifications?page=1&per_page=10' : null,
    requiresAuth: true,
    swrConfig: { refreshInterval: isOpen ? refreshInterval : 0 },
  });

  const unreadCount = countData?.data?.count ?? 0;
  const notifications = listData?.data ?? [];

  const markAsRead = useCallback(
    async (id: number) => {
      try {
        await fetcher({
          url: `/v1/account/notifications/${id}/read`,
          options: { method: 'PATCH' },
          requiresAuth: true,
        });
      } finally {
        mutateCount();
        mutateList();
      }
    },
    [mutateCount, mutateList]
  );

  const markAllAsRead = useCallback(async () => {
    try {
      await fetcher({
        url: '/v1/account/notifications/mark-all-read',
        options: { method: 'POST' },
        requiresAuth: true,
      });
    } finally {
      mutateCount();
      mutateList();
    }
  }, [mutateCount, mutateList]);

  return { unreadCount, notifications, isLoading, markAsRead, markAllAsRead };
};
